import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { getApiUrl } from '../utils/api';

const API_URL = getApiUrl();

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Create API instance that always uses latest token
  const api = useCallback(() => {
    const currentToken = token || localStorage.getItem('token');
    return axios.create({
      baseURL: API_URL,
      headers: currentToken ? { Authorization: `Bearer ${currentToken}` } : {}
    });
  }, [token]);

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          const instance = axios.create({
            baseURL: API_URL,
            headers: { Authorization: `Bearer ${storedToken}` },
            timeout: 8000
          });
          const res = await instance.get('/auth/me');
          setUser(res.data);
          setToken(storedToken);
        } catch (err) {
          console.error('Token validation failed:', err);
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    validateToken();
  }, []);

  const login = async (email, password) => {
    const instance = axios.create({ baseURL: API_URL });
    const res = await instance.post('/auth/login', { email, password });
    const { access_token, user: userData } = res.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name) => {
    const instance = axios.create({ baseURL: API_URL });
    const res = await instance.post('/auth/register', { email, password, name });
    const { access_token, user: userData } = res.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  }, []);

  const forgotPassword = async (email) => {
    const instance = axios.create({ baseURL: API_URL });
    const res = await instance.post('/auth/forgot-password', { email });
    return res.data;
  };

  const resetPassword = async (resetToken, newPassword) => {
    const instance = axios.create({ baseURL: API_URL });
    const res = await instance.post('/auth/reset-password', { token: resetToken, new_password: newPassword });
    return res.data;
  };

  const value = useMemo(() => ({
    user,
    token,
    loading,
    isAuthenticated: !!user && !!token,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    api
  }), [user, token, loading, logout, api]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
